import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import {
    Dialog,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    IconButton,
    Typography,
    Box,
    Divider,
    Collapse,
    CircularProgress
} from '@mui/material';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import LabelIcon from '@mui/icons-material/Label';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import ErrorHandler from '@Root/ErrorHandler';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import LabelCardService from '@Helpers/URLHelpers/WeboutService/LabelCardService';
import './LabelCardForm.scss';

export default function LabelCardForm(props) {

    const [loading, setLoading] = useState(false);
    const [name, setName] = useState(!!props?.item ? (props?.item?.name || '') : '');
    const [description, setDescription] = useState(!!props?.item ? (props?.item?.remarks || '') : '');
    const [remarks, setRemarks] = useState('');
    const [showAdvanced, setShowAdvanced] = useState(false);
    const errorHandler = new ErrorHandler();
    const labelCardService = new LabelCardService();
    const { currentProjectId } = useReduxProject();
    const className = 'labelCardForm';
    const isUpdateForm = props?.type === 'update';

    const handleNameChange = (e) => {
        setName(DOMPurify.sanitize(e.target.value));
    };

    const handleDescriptionChange = (e) => {
        setDescription(DOMPurify.sanitize(e.target.value));
    };

    const handleRemarksChange = (e) => {
        setRemarks(DOMPurify.sanitize(e.target.value));
    };

    const getLabelColor = (value) => {
        if (!value) return '#2563eb';

        const hash = value
            .split('')
            .reduce((acc, char) => char.charCodeAt(0) + acc, 0);

        const colors = [
            '#2563eb',
            '#16a34a',
            '#9333ea',
            '#dc2626',
            '#ea580c',
            '#0891b2'
        ];

        return colors[hash % colors.length];
    };

    const handleSubmit = async (e) => {
        try {
            e?.preventDefault();

            if (!name.trim()) {
                return;
            }

            setLoading(true);

            const labelCardObj = {
                projectId: currentProjectId,
                name: name.trim(),
                remarks: description.trim() || '',
            };
            let response;
            if(isUpdateForm) {
                let body = {
                    labelCard: {
                        ...labelCardObj,
                        id: props?.item?.id
                    },
                }
                response = await labelCardService.updateLabelCard(body);
            } else {
                response = await labelCardService.createLabelCard(labelCardObj);
            }

            if (response.status < 400) {
                SnackbarManager.show(response.message, 'success');

                await props.reloadData();

                props.closePopup();
            } else {
                SnackbarManager.show(response.message, 'error');
            }
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (loading) return;
        props.closePopup();
    };

    const labelColor = getLabelColor(name);

    return (
        <Dialog
            open={props.open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            className={className}
            PaperProps={{
                className: `${className}__paper`
            }}
        >
            {/* ================= HEADER ================= */}
            <div className={`${className}__header`}>
                <div className={`${className}__header__identity`}>
                    <div
                        className={`${className}__header__icon`}
                        style={{
                            background: `${labelColor}15`,
                            color: labelColor
                        }}
                    >
                        <LabelIcon />
                    </div>

                    <div className={`${className}__header__text`}>
                        <Typography
                            className={`${className}__header__title`}
                        >
                            {isUpdateForm ? 'Update' : 'Create New'} Filter
                        </Typography>

                        <Typography
                            className={`${className}__header__subtitle`}
                        >
                            {isUpdateForm ? 'Update' : 'Create'} a reusable filter for your contacts
                        </Typography>
                    </div>
                </div>

                <IconButton
                    onClick={handleClose}
                    disabled={loading}
                    className={`${className}__close`}
                    size="small"
                >
                    <CloseIcon />
                </IconButton>
            </div>

            <Divider />

            <DialogContent className={`${className}__content`}>
                <form
                    id="label-card-form"
                    onSubmit={handleSubmit}
                >
                    <div className={`${className}__field-section`}>
                        <div className={`${className}__field-heading`}>
                            <div
                                className={`${className}__field-heading__icon`}
                            >
                                <LabelIcon />
                            </div>

                            <div>
                                <Typography
                                    className={`${className}__field-heading__title`}
                                >
                                    Filter name
                                    <span>*</span>
                                </Typography>

                                <Typography
                                    className={`${className}__field-heading__description`}
                                >
                                    Give your filter a recognizable name.
                                </Typography>
                            </div>
                        </div>

                        <TextField
                            value={name}
                            onChange={handleNameChange}
                            placeholder="e.g. High Value Customers"
                            required
                            fullWidth
                            variant="outlined"
                            inputProps={{
                                maxLength: 200
                            }}
                            className={`${className}__input`}
                            helperText={`${name.length}/200`}
                        />
                    </div>

                    <div className={`${className}__field-section`}>
                        <div className={`${className}__field-heading`}>
                            <div
                                className={`${className}__field-heading__icon`}
                            >
                                <DescriptionOutlinedIcon />
                            </div>

                            <div>
                                <Typography
                                    className={`${className}__field-heading__title`}
                                >
                                    Description
                                </Typography>

                                <Typography
                                    className={`${className}__field-heading__description`}
                                >
                                    Add some context about this filter.
                                </Typography>
                            </div>
                        </div>

                        <TextField
                            value={description}
                            onChange={handleDescriptionChange}
                            placeholder="Describe what this filter represents..."
                            fullWidth
                            multiline
                            minRows={3}
                            maxRows={5}
                            variant="outlined"
                            className={`${className}__input`}
                            inputProps={{
                                maxLength: 500
                            }}
                            helperText={
                                <span
                                    className={`${className}__helper-row`}
                                >
                                    <span>
                                        Optional
                                    </span>

                                    <span>
                                        {description.length}/500
                                    </span>
                                </span>
                            }
                        />
                    </div>
                </form>
            </DialogContent>

            <Divider />
            <DialogActions className={`${className}__footer`}>
                <div className={`${className}__footer__hint`}>
                    <span className={`${className}__footer__dot`} />
                    <span>
                        {name.trim()
                            ? 'Ready to ' + (isUpdateForm ? 'update' : 'create') + ' your filter'
                            : 'Filter name is required'}
                    </span>
                </div>

                <div className={`${className}__footer__actions`}>
                    <Button
                        onClick={handleClose}
                        disabled={loading}
                        variant="outlined"
                        className={`${className}__cancel`}
                    >
                        Cancel
                    </Button>

                    <Button
                        type="submit"
                        form="label-card-form"
                        variant="contained"
                        disabled={loading || !name.trim()}
                        className={`${className}__submit`}
                    >
                        {loading ? (
                            <>
                                <CircularProgress
                                    size={17}
                                    thickness={4}
                                    className={`${className}__submit__loader`}
                                />

                                {isUpdateForm ? 'Updating...' : 'Creating...'}
                            </>
                        ) : (
                            <>
                                <LabelIcon fontSize="small" />
                                {isUpdateForm ? 'Update' : 'Create'} Filter
                            </>
                        )}
                    </Button>
                </div>
            </DialogActions>
        </Dialog>
    );
}
